// STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · Studio 미리보기 Overlay 무대 + 배치 편집.
//   · 상품 카드(LivePresetOverlay) · QR · 사이즈표 · 공지 이미지 · 지난 상품 (EgressTemplate 공통 컴포넌트 REUSE).
//   · 드래그 중 API 호출 0 · 드래그 종료 시 1회 저장 (/seller/live/overlay-layout).
//   · 테스트 모드: 서버 쓰기 0 — localStorage 에만 저장.
//   · MOBILE-FINAL-FIX-1: 방송 전·편집 중 데이터 없는 요소는 '미리보기 샘플' 로 실제 크기 렌더 (저장 X).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import api from '@/lib/api';
import LivePresetOverlay from '@/components/live/LivePresetOverlay';
import OverlayFreeBox from '@/components/live/OverlayFreeBox';
import { OverlayQrCard, OverlaySizeTableCard, OverlayNoticeImage, OverlayNoticeCard, OverlayTimerCard, sizeTableHasData } from '@/components/live/OverlayExtras';
import { timerViewOf } from '@/lib/timerLocal';
import {
  ELEMENT_LABELS, getRect, baseFrac, cardOrientation, pastOrientation, initialRect, clampRect, isFluid, LAYOUT_ELEMENTS,
  TOP_KEYS, displayMode, combinedOrientation, combinedItems, mergeLayout,
} from '@/lib/overlayLayout';
import OverlayCombinedCard from '@/components/live/OverlayCombinedCard';
import { SAMPLE_PRODUCT, SAMPLE_PAST_PRODUCT, SAMPLE_SIZE_TABLE, SAMPLE_NOTICE_IMAGE, SAMPLE_NOTICE, sampleQrUrl } from '@/lib/overlaySamples';

const TEST_KEY = 'livemoa_test_overlay_layout';
const SAMPLE_TIMER = { label: '타이머', status: 'paused', remaining_seconds: 300, visible: true };

export const useOverlayLayout = ({ scheduleId, isTestMode, liveCtx }) => {
  const [layout, setLayout] = useState(() => {
    if (!isTestMode) return null;
    try { return JSON.parse(localStorage.getItem(TEST_KEY) || 'null'); } catch { return null; }
  });
  const [editing, setEditing] = useState(false);
  // 저장 범위 — 'default'(판매자 기본 · 이후 방송 자동) | 'broadcast'(이 방송만).
  const [saveScope, setSaveScope] = useState('default');
  // 라모 음성 전용 · 현재 세션에만 적용 (저장 X · 재진입 시 저장값 복원).
  const [sessionTop, setSessionTopState] = useState(null);
  const setSessionTop = useCallback((patch) => setSessionTopState((s) => ({ ...(s || {}), ...patch })), []);
  const serverJson = JSON.stringify(liveCtx?.overlay_layout || null);
  // STUDIO-OVERLAY-EDIT-CANCEL-FIX-1 · 편집 중 저장 지연 · 진입 시 스냅샷 → 취소 시 롤백.
  const editingRef = useRef(false);
  const snapRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  editingRef.current = editing;

  useEffect(() => {
    if (isTestMode || editing) return;
    if (liveCtx?.overlay_layout) { setLayout(liveCtx.overlay_layout); return; }
    let alive = true;
    // 방송 전: 판매자 기본 + 이 방송 설정(기존 today 목록) 병합 — 방송 설정 우선.
    Promise.all([
      api.get('/seller/settings').catch(() => null),
      scheduleId ? api.get('/seller/broadcasts/today').catch(() => null) : null,
    ]).then(([s, t]) => {
      if (!alive) return;
      const own = (Array.isArray(t?.data) ? t.data : []).find((b) => b.schedule_id === scheduleId)?.overlay_layout;
      const m = mergeLayout(s?.data?.overlay_default_layout, own);
      if (m) setLayout(m);
    });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverJson, isTestMode, scheduleId]);

  const persist = useCallback(async (next, orient, extra = {}, force = false) => {
    if (editingRef.current && !force) { setDirty(true); return; }
    if (isTestMode) {
      try { localStorage.setItem(TEST_KEY, JSON.stringify(next)); } catch {}
      return;
    }
    const top = Object.fromEntries(TOP_KEYS.filter((k) => next?.[k] != null).map((k) => [k, next[k]]));
    try {
      await api.put('/seller/live/overlay-layout', {
        schedule_id: scheduleId || undefined, orientation: orient,
        elements: next?.[orient] || {}, card_orientation: cardOrientation(next),
        past_card_orientation: pastOrientation(next), ...top,
        scope: saveScope === 'broadcast' && scheduleId ? 'broadcast' : 'default', ...extra,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || '배치 저장 실패'); }
  }, [isTestMode, scheduleId, saveScope]);
  // 표시 방식·통합 형태·포함 항목·기본 프리셋 (좌표 무변경).
  const setTop = useCallback((orient, patch) => {
    setSessionTopState((s) => {
      if (!s) return s;
      const rest = Object.fromEntries(Object.entries(s).filter(([k]) => !(k in patch)));
      return Object.keys(rest).length ? rest : null;
    });
    setLayout((l) => {
      const next = { ...(l || {}), ...patch };
      persist(next, orient);
      return next;
    });
  }, [persist]);

  const updateRect = useCallback((orient, el, rect) => {
    setLayout((l) => ({ ...(l || {}), [orient]: { ...((l || {})[orient] || {}), [el]: rect } }));
  }, []);
  const commitRect = useCallback((orient, el, rect) => {
    setLayout((l) => {
      const next = { ...(l || {}), [orient]: { ...((l || {})[orient] || {}), [el]: rect } };
      persist(next, orient);
      return next;
    });
  }, [persist]);
  // 저장 위치가 없어도 형태 전환이 즉시 반영되도록 카드 rect 를 함께 확정 (송출도 동일 rect 사용).
  const setCardOrientation = useCallback((orient, v, combo) => {
    setLayout((l) => {
      const base = { ...(l || {}), card_orientation: v };
      const cur = getRect(base, orient, 'card') || initialRect(base, orient, 'card', { combo });
      const next = { ...base, [orient]: { ...(base[orient] || {}), card: { ...cur, w: baseFrac(orient, 'card', base) } } };
      persist(next, orient);
      return next;
    });
  }, [persist]);
  // 지난 상품 카드 형태 — 현재 상품 카드와 독립 저장 (저장 위치는 유지, 폭만 새 형태 기준폭으로).
  const setPastOrientation = useCallback((orient, v, combo) => {
    setLayout((l) => {
      const base = { ...(l || {}), past_card_orientation: v };
      const cur = getRect(base, orient, 'past_card') || initialRect(base, orient, 'past_card', { combo });
      const next = { ...base, [orient]: { ...(base[orient] || {}), past_card: { ...cur, w: baseFrac(orient, 'past_card', base) } } };
      persist(next, orient);
      return next;
    });
  }, [persist]);
  const reset = useCallback((orient) => {
    setLayout((l) => {
      const next = { ...(l || {}) };
      delete next[orient];
      if (isTestMode) persist(next, orient);
      else persist(next, orient, { reset: true });
      return next;
    });
    toast.success('기본 위치로 복원했어요');
  }, [isTestMode, persist]);

  // 편집 종료 — 화면에 보이던 배치(미이동 요소의 기본값 포함)를 판매자 기본 배치로 1회 저장.
  const finish = useCallback((orient, noticeSize, combo, preLive) => {
    setLayout((l) => {
      const cur = { ...((l || {})[orient] || {}) };
      LAYOUT_ELEMENTS.forEach((el) => { if (!getRect(l, orient, el)) cur[el] = initialRect(l, orient, el, { noticeSize, combo, preLive }); });
      const next = { ...(l || {}), [orient]: cur };
      persist(next, orient, {}, true);
      return next;
    });
    editingRef.current = false;
    setDirty(false);
    setEditing(false);
    toast.success(isTestMode ? '배치 저장 완료 (이 브라우저만)'
      : saveScope === 'broadcast' ? '배치 저장 완료 · 이 방송만' : '배치 저장 완료 · 다음 방송에도 자동 적용');
  }, [persist, isTestMode, saveScope]);

  const begin = useCallback(() => {
    setLayout((l) => { snapRef.current = l; return l; });
    setDirty(false);
    editingRef.current = true;
    setEditing(true);
  }, []);
  // 취소 — 이번 편집의 배치만 진입 전 값으로 복원 (서버·저장값 무변경).
  const cancel = useCallback(() => {
    setLayout(snapRef.current);
    setDirty(false);
    editingRef.current = false;
    setEditing(false);
  }, []);

  const view = useMemo(() => (sessionTop ? { ...(layout || {}), ...sessionTop } : layout), [layout, sessionTop]);
  return { layout: view, setSessionTop, editing, setEditing, updateRect, commitRect, setCardOrientation, setPastOrientation, reset, finish,
           setTop, saveScope, setSaveScope, begin, cancel, dirty };
};

const OFF_BTN = 'bg-white text-gray-800 border-black/10';
const CancelControl = ({ ol }) => {
  const [asking, setAsking] = useState(false);
  if (asking) {
    return (
      <div className="w-full flex items-center justify-center gap-1.5 pt-1" data-testid="overlay-layout-cancel-confirm">
        <span className="text-[11px] font-black text-gray-700">저장하지 않고 나갈까요?</span>
        <button type="button" onClick={() => { setAsking(false); ol.cancel(); }}
                className="min-h-[32px] px-3 rounded-full text-[11px] font-black bg-[#FF3B30] text-white" data-testid="overlay-layout-cancel-yes">나가기</button>
        <button type="button" onClick={() => setAsking(false)}
                className="min-h-[32px] px-3 rounded-full text-[11px] font-black bg-white text-gray-800 border border-black/10" data-testid="overlay-layout-cancel-no">계속 편집</button>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => (ol.dirty ? setAsking(true) : ol.cancel())} aria-label="배치 편집 닫기"
            className="absolute -top-2.5 -right-2.5 w-9 h-9 rounded-full bg-white text-gray-800 border border-black/10 shadow-md inline-flex items-center justify-center"
            data-testid="overlay-layout-close"><X size={16} /></button>
  );
};

const Toolbar = ({ ol, orient, isTestMode }) => {
  const combined = displayMode(ol.layout) === 'combined';
  const co = combined ? combinedOrientation(ol.layout) : cardOrientation(ol.layout);
  const setCo = (v) => (combined ? ol.setTop(orient, { combined_orientation: v }) : ol.setCardOrientation(orient, v, ol.combo));
  const btn = 'min-h-[36px] px-3 py-1.5 rounded-full text-[12px] font-black border';
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-[72px] lg:top-3 z-50 pointer-events-auto flex flex-wrap items-center justify-center gap-1.5 px-2 py-1.5 rounded-2xl bg-white/90 w-max border border-black/5 shadow-lg backdrop-blur-md max-w-[94%]"
         data-testid="overlay-layout-toolbar">
      <span className="text-[10px] font-black text-gray-500 px-1">배치 편집 · {orient}</span>
      {['horizontal', 'vertical'].map((v) => (
        <button key={v} type="button" onClick={() => setCo(v)}
                className={`${btn} ${co === v ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : OFF_BTN}`}
                data-testid={`overlay-layout-card-${v}`}>{v === 'horizontal' ? '가로형' : '세로형'}</button>
      ))}
      {['horizontal', 'vertical'].map((v) => (
        <button key={`p-${v}`} type="button" onClick={() => ol.setPastOrientation(orient, v, ol.combo)}
                className={`${btn} ${pastOrientation(ol.layout) === v ? 'bg-gray-700 text-white border-gray-700' : OFF_BTN}`}
                data-testid={`overlay-layout-past-${v}`}>{v === 'horizontal' ? '지난 가로' : '지난 세로'}</button>
      ))}
      <button type="button" onClick={() => ol.reset(orient)} className={`${btn} ${OFF_BTN}`}
              data-testid="overlay-layout-reset">기본 위치 복원</button>
      <button type="button" onClick={() => ol.finish(orient, ol.noticeSize, ol.combo, ol.preLive)} className={`${btn} bg-gray-900 text-white border-gray-900`}
              data-testid="overlay-layout-done">{isTestMode ? '배치 완료 · 저장 (이 브라우저)' : '배치 완료 · 저장'}</button>
      <CancelControl ol={ol} />
    </div>
  );
};

const SampleTag = ({ children = '미리보기 샘플' }) => (
  <span style={{ position: 'absolute', top: -9, right: 6, zIndex: 2, padding: '1px 7px', borderRadius: 999, background: '#FFB020',
                 color: '#111', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }} data-testid="studio-overlay-sample-tag">{children}</span>
);

const PastCard = ({ past, sample, orientation, width }) => (
  <div key={past.product.product_id} style={{ position: 'relative' }}
       className={past.effect === 'fade' ? 'animate-in fade-in duration-500' : 'animate-in slide-in-from-right duration-300'}
       data-testid="studio-overlay-past-card" data-orientation={orientation}>
    <span style={{ position: 'absolute', top: -7, left: 6, zIndex: 2, padding: '0 6px', borderRadius: 999, background: '#4B5563',
                   color: '#fff', fontSize: 9, fontWeight: 800, lineHeight: '14px' }} data-testid="studio-overlay-past-tag">
      지난 상품{past.total ? ` ${past.index + 1}/${past.total}` : ''}
    </span>
    {sample && <SampleTag />}
    <div style={{ opacity: 0.92, filter: 'saturate(0.85)' }}>
      {orientation === 'vertical'
        ? <LivePresetOverlay preset="default" product={past.product} free orientation="vertical" imageFit="contain" />
        : <OverlayCombinedCard product={past.product} preset="default" orientation="horizontal" width={width}
                               testid="studio-overlay-past-banner" />}
    </div>
  </div>
);

// LIVE-NOTICE-DESCRIPTION-FINAL-FIX-1 · 판매자 화면 전용 공지 감추기 (브라우저 로컬 · 외부 송출·소비자 무관).
const NOTICE_HIDE_KEY = 'lm_studio_notice_hidden';
export const useStudioNoticeHidden = () => {
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(NOTICE_HIDE_KEY) === '1'; } catch { return false; } });
  useEffect(() => {
    const on = () => { try { setHidden(localStorage.getItem(NOTICE_HIDE_KEY) === '1'); } catch {} };
    window.addEventListener('lm-studio-notice-hidden', on);
    return () => window.removeEventListener('lm-studio-notice-hidden', on);
  }, []);
  const set = useCallback((v) => {
    try { localStorage.setItem(NOTICE_HIDE_KEY, v ? '1' : '0'); } catch {}
    window.dispatchEvent(new Event('lm-studio-notice-hidden'));
  }, []);
  return [hidden, set];
};

export const StudioOverlayStage = ({ liveCtx, isLive, draftPreset, orient, pcAspect, ol, isTestMode = false, combo = false,
                                     showPrefs = null, pastPreview = null, suppressed = false, productOverlay = true, extPreview = false }) => {
  const { layout, editing } = ol;
  // STUDIO-SELF-NOTICE-IMAGE-REMOVE-1 · TEST 자사라이브 공지는 문구 전용 (기존 이미지 참조 비노출).
  if (liveCtx?.live_notice?.image_url) {
    liveCtx = { ...liveCtx, live_notice: { ...liveCtx.live_notice, image_url: null, image_size: null } };
  }
  const isPortrait = orient === '9:16';
  const noticeSize = liveCtx?.live_notice?.image_size;
  // 작업 패널이 열려 있으면 Overlay·편집 테두리 숨김 (편집 상태는 유지 · 편집 툴바만 표시).
  if (suppressed) {
    return editing ? (
      <div className="absolute inset-0 pointer-events-none z-[20]" data-testid="studio-overlay-suppressed">
        {/* 작업 패널(z-40 이상) 아래에 두어 입력 화면을 가리지 않음 — 편집 상태는 유지. */}
        <Toolbar ol={{ ...ol, noticeSize, combo, preLive: !isLive }} orient={orient} isTestMode={isTestMode} />
      </div>
    ) : null;
  }
  // LIVEMOA-LIVE-TEST-PREVIEW-INTEGRATION-1 · STEP A · 방송 준비 화면은 Overlay 기본 숨김 (배치 편집 중에만 표시 · 저장값 무변경).
  if (!isLive && !editing) return null;
  const useSample = !isLive || editing;
  const active = liveCtx?.active_product || null;
  const firstListed = Array.isArray(liveCtx?.live_products) ? liveCtx.live_products[0] : null;
  const display = liveCtx?.active_product_display || {};
  const hidden = display?.mode === 'hidden';
  const liveCard = isLive && active && !hidden;
  const preset = liveCard ? (display?.preset || 'default') : draftPreset;
  const product = liveCard ? active : (active || firstListed || (useSample ? SAMPLE_PRODUCT : null));
  const preLive = !isLive;
  const defRect = (el) => initialRect(layout, orient, el, { noticeSize, combo, preLive });
  const rectOf = (el) => (editing ? defRect(el) : getRect(layout, orient, el));
  const boxProps = (el) => ({
    rect: rectOf(el), base: baseFrac(orient, el, layout), editing, label: ELEMENT_LABELS[el],
    clamp: (r) => clampRect(r, orient, el, layout),
    onChange: (r) => ol.updateRect(orient, el, r), onCommit: (r) => ol.commitRect(orient, el, r),
    testid: `studio-overlay-free-${el}`, zIndex: editing ? 40 : 10,
  });
  const withDefault = (el) => ({ ...boxProps(el), rect: rectOf(el) || defRect(el) });

  // 저장 위치가 없으면 기본 rect(화면 중앙) — 하단 카메라 조작 버튼과 겹치지 않게.
  const cardRect = rectOf('card') || defRect('card');
  const qr = liveCtx?.active_product_qr;
  // 실제 LIVE: 서버 url_path 가 있을 때만 · 테스트: 샘플 QR (표기 구분).
  const realQr = (qr?.visible && active?.product_id)
    ? (qr.url_path ? `${window.location.origin}${qr.url_path}`
      : (isTestMode ? `${window.location.origin}/product/${active.product_id}` : '')) : '';
  const sampleQr = !realQr && (editing || (!isLive && showPrefs?.qr));
  const qrUrl = realQr || (sampleQr ? sampleQrUrl() : '');
  const qrLabel = sampleQr ? '미리보기 샘플 QR' : (isTestMode ? '테스트 샘플 QR' : undefined);
  const realSt = !!(display?.size_table_visible && sizeTableHasData(active?.size_table));
  const sampleSt = !realSt && (editing || (!isLive && showPrefs?.size_table));
  const sizeTable = realSt ? active.size_table
    : (sampleSt ? (sizeTableHasData(product?.size_table) ? product.size_table : SAMPLE_SIZE_TABLE) : null);
  const realNoticeImg = liveCtx?.live_notice?.image_url || '';
  const noticeImg = realNoticeImg || (editing ? SAMPLE_NOTICE_IMAGE : '');
  const past = pastPreview?.product ? pastPreview
    : (editing ? { product: SAMPLE_PAST_PRODUCT, effect: 'fade', index: 0, total: 0 } : null);
  // 통합 카드 모드 — 상품·사이즈표·QR 을 하나로 (개별 박스 미렌더 · 개별 좌표 보존).
  // CONSOLIDATION-1 · 판매자 기본 화면: 상품 카드·통합·QR 숨김 (상품설명·공지·타이머·지난 상품 유지 · 설정 무변경).
  const showProduct = productOverlay || editing;
  const combined = showProduct && displayMode(layout) === 'combined';
  const cEl = `combined_${preset === 'mini' ? 'mini_' : ''}${combinedOrientation(layout)}`;
  const cItems = combinedItems(layout);
  // LIVE-OVERLAY-PREVIEW-CONSISTENCY-FIX-1 · 판매자 일반 화면에서는 상품설명(사이즈표) 숨김 · 배치 편집 중에만 표시.
  const cSt = !cItems.size_table || !(editing || extPreview) ? null
    : (sizeTableHasData(product?.size_table) ? product.size_table : (product?.__sample ? SAMPLE_SIZE_TABLE : null));
  const cQr = !cItems.qr || !product ? ''
    : (realQr || (product.__sample ? sampleQrUrl() : `${window.location.origin}/product/${product.product_id}`));
  const cQrLabel = product?.__sample ? '미리보기 샘플 QR' : (isTestMode ? '테스트 샘플 QR' : undefined);
  const showCombined = combined && product && !(hidden && isLive && !editing);
  // 타이머: 실행/일시정지 중일 때만 실제 표시 (00:00 종료 시 숨김 · 1초 단위 갱신은 카드 내부).
  const tv = timerViewOf(liveCtx?.timer, Date.now());
  const timerLive = !!(tv && (tv.status !== 'finished' || liveCtx?.timer?.status === 'running'));
  const innerCls = pcAspect === '16:9'
    ? 'lg:w-full lg:h-auto lg:max-h-full lg:aspect-video'
    : 'lg:h-full lg:w-auto lg:max-w-full lg:aspect-[9/16]';

  return (
    <div className={`absolute inset-0 pointer-events-none flex items-center justify-center ${editing ? 'z-[60]' : 'z-[15]'}`} data-testid="studio-overlay-stage"
         data-orient={orient} data-editing={editing ? '1' : '0'} data-preview={useSample ? '1' : '0'}>
      <div className={`relative w-full h-full ${innerCls}`}>
        {showCombined && (
          <OverlayFreeBox {...withDefault(cEl)} fluid testid="studio-overlay-free-combined">
            {(cw) => (
              <div style={{ position: 'relative' }}>
                {product.__sample && <SampleTag />}
                <OverlayCombinedCard product={product} preset={preset} orientation={combinedOrientation(layout)}
                                     sizeTable={cSt} qrUrl={cQr} qrLabel={cQrLabel} width={cw} isPortrait={isPortrait} />
              </div>
            )}
          </OverlayFreeBox>
        )}
        {showProduct && !combined && (cardRect ? (
          <OverlayFreeBox {...boxProps('card')} rect={cardRect} fluid={isFluid('card', layout)}>
            {product && (
              <div style={{ position: 'relative' }}>
                {product.__sample && <SampleTag />}
                <LivePresetOverlay preset={preset || 'default'} product={product} hidden={hidden && !editing && isLive}
                                   free orientation={cardOrientation(layout)} />
              </div>
            )}
          </OverlayFreeBox>
        ) : (
          <>
            {product?.__sample && <div className="absolute left-3 bottom-[22%] z-[16]"><SampleTag /></div>}
            <LivePresetOverlay preset={preset || 'default'} product={product} position={display?.position || 'bottom'} hidden={hidden && isLive} />
          </>
        ))}
        {showProduct && !combined && qrUrl && (
          <OverlayFreeBox {...withDefault('qr')}>
            {(cw) => <OverlayQrCard url={qrUrl} size={Math.max(60, Math.round(cw - 24))} isPortrait={isPortrait} label={qrLabel} />}
          </OverlayFreeBox>
        )}
        {(editing || extPreview) && !combined && sizeTable && (
          <OverlayFreeBox {...withDefault('size_table')}>
            {(cw) => (
              <div style={{ position: 'relative' }}>
                {sampleSt && <SampleTag />}
                <OverlaySizeTableCard sizeTable={sizeTable} isPortrait={isPortrait} free
                                      vw={(n) => `${(n * cw) / baseFrac(orient, 'size_table', layout) / 100}px`} />
              </div>
            )}
          </OverlayFreeBox>
        )}
        {/* LIVE-EXTERNAL-OVERLAY-PREVIEW-FINAL-1 · 공지 문구·이미지는 배치 편집 또는 송출 미리보기에서만 (일반 판매자 화면 미노출 · 원본 크기 = 송출과 동일). */}
        {(editing || extPreview) && noticeImg && (rectOf('notice_image') || editing) && (
          <OverlayFreeBox {...boxProps('notice_image')}>
            <OverlayNoticeImage src={noticeImg} />
          </OverlayFreeBox>
        )}
        {past && (
          <OverlayFreeBox {...withDefault('past_card')} fluid={isFluid('past_card', layout)}>
            {(cw) => <PastCard past={past} sample={!!past.product.__sample} orientation={pastOrientation(layout)} width={cw} />}
          </OverlayFreeBox>
        )}
        {/* STUDIO-NOTICE-TIMER-LAYOUT-FIX-1 · 공지 문구 자유 배치 (저장 위치 또는 편집 중 · 없으면 기존 고정 위치). */}
        {/* 판매자 화면 공지: 감추기 시 숨김 · 평소 50% 축소 표시 (편집 중엔 원래 크기로 이동·크기 조절). */}
        {(editing || extPreview) && ((editing || rectOf('notice')) ? ((editing || liveCtx?.live_notice) && (
          <OverlayFreeBox {...boxProps('notice')} fluid>
            {(cw) => (
            <div style={{ position: 'relative' }}>
              {editing && !liveCtx?.live_notice?.text && <SampleTag />}
              <OverlayNoticeCard free isPortrait={isPortrait} showImage={!rectOf('notice_image')} stageW={cw / (rectOf('notice')?.w || 1)} boxW={cw}
                                 notice={editing && !liveCtx?.live_notice?.text ? { ...(liveCtx?.live_notice || {}), ...SAMPLE_NOTICE } : liveCtx.live_notice} />
            </div>
            )}
          </OverlayFreeBox>
        )) : (liveCtx?.live_notice && (
          <OverlayNoticeCard notice={liveCtx.live_notice} isPortrait={isPortrait} showImage={!rectOf('notice_image')} />
        )))}
        {/* TIMER-EDIT-END-FIX-1 · 저장 위치 있거나 편집 중이면 배치 박스 (편집 중 미실행이면 샘플) · 카운트다운 상태 무관. */}
        {(editing || rectOf('timer')) ? ((editing || timerLive) && (
          <OverlayFreeBox {...withDefault('timer')} fluid>
            <OverlayTimerCard free isPortrait={isPortrait} viewOf={timerViewOf}
                              sample={!timerLive} timer={timerLive ? liveCtx.timer : SAMPLE_TIMER} />
          </OverlayFreeBox>
        )) : (liveCtx?.timer && <OverlayTimerCard timer={liveCtx.timer} isPortrait={isPortrait} viewOf={timerViewOf} />)}
        {editing && <Toolbar ol={{ ...ol, noticeSize, combo, preLive: !isLive }} orient={orient} isTestMode={isTestMode} />}
      </div>
    </div>
  );
};

export default StudioOverlayStage;
